<template>
    <section class="at-card" :class="{ 'at-card--saved': isDisabled }">
        <header class="at-card-header">
            <div class="at-card-header-icon">
                <i class="fal fa-envelope-open-text"></i>
            </div>
            <div>
                <h2 class="at-card-title">
                    {{ $t('template.section_metadata') }}
                    <span v-if="!isEditMode && index !== undefined && index !== null" class="at-card-index">#{{ index + 1 }}</span>
                </h2>
                <p class="at-card-desc">
                    {{ isEditMode ? $t('template.edit_subtitle') : $t('template.create_subtitle') }}
                </p>
            </div>
            <span v-if="isDisabled" class="at-card-saved">
                <i class="fal fa-check-circle"></i>
                {{ $t('template.saved_toast') }}
            </span>
        </header>

        <form class="at-form" @submit.prevent>
            <div class="at-grid-2">
                <div class="at-field">
                    <label class="at-label" for="at-reference">{{ $t('template.reference') }}</label>
                    <input
                        id="at-reference"
                        v-model="reference"
                        type="text"
                        class="at-input"
                        :placeholder="$t('template.reference_placeholder')"
                    />
                    <p v-if="referenceError" class="at-error">{{ referenceError }}</p>
                </div>

                <div class="at-field">
                    <label class="at-label" for="at-form">{{ $t('template.form') }}</label>
                    <div class="at-select-wrapper">
                        <select id="at-form" v-model="selectedForm" class="at-input at-select">
                            <option
                                v-for="option in formOptions"
                                :key="option.id"
                                :value="option.id"
                            >
                                {{ option.name }}
                            </option>
                        </select>
                        <i class="fal fa-chevron-down at-select-caret"></i>
                    </div>
                    <p v-if="selectedFormError" class="at-error">{{ selectedFormError }}</p>
                </div>
            </div>

            <div class="at-field">
                <label class="at-label" for="at-subject">{{ $t('template.subject') }}</label>
                <input
                    id="at-subject"
                    v-model="subject"
                    type="text"
                    class="at-input"
                    :placeholder="$t('template.subject_placeholder')"
                />
                <p v-if="subjectError" class="at-error">{{ subjectError }}</p>
            </div>

            <div class="at-field">
                <label class="at-label">{{ $t('template.section_content') }}</label>
                <div class="at-editor">
                    <ckeditor
                        v-model="template"
                        :config="editorConfig"
                        :editor="editor"
                    ></ckeditor>
                </div>
                <p v-if="templateError" class="at-error">{{ templateError }}</p>
            </div>

            <div class="at-actions">
                <button
                    v-if="!isDisabled"
                    type="button"
                    class="at-btn at-btn--ghost"
                    @click.prevent="cancel"
                >
                    {{ $t('template.delete.cancel') }}
                </button>
                <button
                    type="button"
                    class="at-btn at-btn--primary"
                    :disabled="isDisabled"
                    @click.prevent="save"
                >
                    <i class="fal fa-check"></i>
                    {{ $t('template.save') }}
                </button>
            </div>
        </form>
    </section>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { mapActions, mapState } from 'vuex'

export default {
    name: 'AddTemplate',
    props: {
        formOptions: { type: Array, default: () => [] },
        type: { type: String, default: '' },
        space: { type: String, default: '' },
        index: { type: Number, default: null },
        templateToEdit: { type: Object, default: null }
    },
    data() {
        return {
            loading: true,
            subject: null,
            reference: null,
            template: null,
            selectedForm: null,
            editor: ClassicEditor,
            editorConfig: {
                toolbar: {
                    items: [
                        'bold',
                        'italic',
                        'link',
                        '|',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'undo',
                        'redo'
                    ]
                }
            },
            isDisabled: false,
            referenceError: null,
            subjectError: null,
            templateError: null,
            selectedFormError: null
        }
    },
    computed: {
        ...mapState('admin', {
            templates: (state) => state.templates
        }),
        isEditMode() {
            return !!this.templateToEdit
        }
    },
    watch: {
        templateToEdit: {
            immediate: true,
            handler(val) {
                if (val) this.hydrateFromTemplate(val)
            }
        }
    },
    methods: {
        ...mapActions('admin', ['postTemplate', 'editTemplate']),
        hydrateFromTemplate(tpl) {
            this.reference = tpl.response_title || null
            this.subject = tpl.response_subject || null
            this.template = tpl.response_content || null
            this.selectedForm = tpl.form && tpl.form.id ? tpl.form.id : null
        },
        cancel() {
            this.$router.go(-1)
        },
        save() {
            this.templateError = this.template ? null : this.$t('template.field_error')
            this.referenceError = this.reference ? null : this.$t('template.field_error')

            if (!this.template || !this.reference) return

            const payload = {
                form_id: this.selectedForm,
                response_title: this.reference,
                response_content: this.template,
                response_subject: this.subject
            }

            if (this.isEditMode) {
                this.editTemplate({
                    id: this.templateToEdit.id,
                    template: payload
                }).then(() => {
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('template.saved')
                    })
                    this.$router.push('/admin/templates')
                })
            } else {
                this.postTemplate(payload).then(() => {
                    this.isDisabled = true
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('template.saved_toast')
                    })
                })
            }
        }
    }
}
</script>

<style scoped>
.at-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
    transition: border-color var(--v2-transition-fast);
}

.at-card--saved {
    border-color: var(--v2-success);
    background: rgba(16, 185, 129, 0.02);
}

.at-card-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 22px;
    border-bottom: 1px solid var(--v2-border);
}

.at-card-header-icon {
    width: 40px;
    height: 40px;
    border-radius: var(--v2-radius-md);
    background: rgba(8, 145, 178, 0.1);
    color: var(--v2-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1.05rem;
}

.at-card-title {
    margin: 0;
    font-size: 1rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    display: flex;
    align-items: center;
    gap: 8px;
}

.at-card-index {
    font-size: 0.7rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-3);
    background: var(--v2-surface-2);
    padding: 2px 8px;
    border-radius: var(--v2-radius-sm);
    letter-spacing: 0.02em;
}

.at-card-desc {
    margin: 2px 0 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
}

.at-card-saved {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-success);
    background: rgba(16, 185, 129, 0.1);
    padding: 4px 10px;
    border-radius: var(--v2-radius-sm);
}

.at-card-saved i {
    font-size: 0.8rem;
}

.at-form {
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.at-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.at-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.at-label {
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-2);
    letter-spacing: 0.01em;
    margin: 0;
}

.at-input {
    width: 100%;
    box-sizing: border-box;
    height: 38px;
    padding: 0 12px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-1);
    font-family: inherit;
    outline: none;
    transition: border-color var(--v2-transition-fast), box-shadow var(--v2-transition-fast);
}

.at-input:hover {
    border-color: var(--v2-border-strong, #cbd5e1);
}

.at-input:focus {
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12);
}

.at-input::placeholder {
    color: var(--v2-text-4);
}

.at-select-wrapper {
    position: relative;
}

.at-select {
    padding-right: 34px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
    background: var(--v2-surface-0);
}

.at-select-caret {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.75rem;
    color: var(--v2-text-3);
    pointer-events: none;
}

.at-error {
    margin: 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-danger);
    font-weight: var(--v2-font-weight-medium);
}

.at-editor {
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    overflow: hidden;
    transition: border-color var(--v2-transition-fast);
}

.at-editor:focus-within {
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12);
}

.at-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 14px;
    border-top: 1px solid var(--v2-border);
    margin-top: 4px;
}

.at-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 16px;
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    cursor: pointer;
    transition: all var(--v2-transition-fast);
    border: 1px solid transparent;
    font-family: inherit;
}

.at-btn i {
    font-size: 0.8rem;
}

.at-btn--ghost {
    background: transparent;
    border-color: var(--v2-border);
    color: var(--v2-text-2);
}

.at-btn--ghost:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.at-btn--primary {
    background: var(--v2-primary);
    color: #fff !important;
}

.at-btn--primary i {
    color: #fff !important;
}

.at-btn--primary:hover:not(:disabled) {
    background: var(--v2-primary-hover, #0e7490);
    box-shadow: var(--v2-shadow-sm);
}

.at-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
}

@media (max-width: 900px) {
    .at-grid-2 {
        grid-template-columns: 1fr;
    }

    .at-form {
        padding: 16px;
    }

    .at-card-header {
        padding: 14px 16px;
        flex-wrap: wrap;
    }

    .at-card-saved {
        margin-left: 0;
    }
}
</style>

<style>
/* CKEditor alignment with V2 inputs */
.at-editor .ck.ck-editor__main > .ck-editor__editable {
    min-height: 160px;
    border: none;
    box-shadow: none;
    padding: 10px 14px;
}

.at-editor .ck.ck-toolbar {
    border: none;
    border-bottom: 1px solid var(--v2-border);
    background: var(--v2-surface-1);
}

.at-editor .ck-focused,
.at-editor .ck.ck-editor__editable:not(.ck-editor__nested-editable).ck-focused {
    border: none !important;
    box-shadow: none !important;
}
</style>
